import { ArrowLeftRight, Link2, Link2Off, Plus } from 'lucide-react';

import { Button } from '@/components/Button';
import {
  useClientMap,
  useItflowSettings,
  useLinkClient,
  usePullClient,
  usePushClient,
  useUnlinkClient,
} from './useItflow';

/**
 * Which Chronos client is which ITFlow client.
 *
 * Mapping is partial by design, plenty of clients exist on only one side, and
 * the Personal client never appears here at all. Nothing is matched
 * automatically: two clients called "Acme" at different companies is ordinary,
 * and an invoice sent to the wrong one is not a recoverable mistake. Close
 * names are offered as a one-tap suggestion instead.
 */
export function ClientMappingPanel() {
  const settings = useItflowSettings();
  const configured = Boolean(settings.data?.configured);
  const map = useClientMap(configured);
  const link = useLinkClient();
  const unlink = useUnlinkClient();
  const push = usePushClient();
  const pull = usePullClient();

  if (!configured) {
    return (
      <p className="text-small text-text-secondary">
        Connect an ITFlow instance first.
      </p>
    );
  }

  if (map.isLoading) {
    return <div className="h-48 animate-pulse rounded-lg bg-surface-2" aria-hidden />;
  }

  if (map.error) {
    return (
      <p className="text-small text-danger">
        Could not reach ITFlow: {map.error.message}
      </p>
    );
  }

  const data = map.data;
  if (!data) return null;

  const linkByClient = new Map(data.links.map((l) => [l.client_id, l]));
  const linkedRemote = new Set(data.links.map((l) => l.itflow_client_id));
  const remoteById = new Map(data.itflow.map((r) => [r.itflow_client_id, r]));
  const suggestionFor = new Map(data.suggestions.map((s) => [s.client_id, s]));

  const unmappedRemote = data.itflow.filter(
    (r) => !linkedRemote.has(r.itflow_client_id),
  );

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-2">
        <h3 className="text-small font-medium text-text-secondary">Chronos clients</h3>
        <ul className="flex flex-col gap-2">
          {data.chronos.map((client) => {
            const mapped = linkByClient.get(client.id);
            const suggestion = suggestionFor.get(client.id);
            const suggested = suggestion
              ? remoteById.get(suggestion.itflow_client_id)
              : undefined;

            return (
              <li
                key={client.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface-1 p-3"
              >
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate text-body text-text">{client.name}</span>
                  {mapped ? (
                    <span className="flex items-center gap-1.5 text-small text-success">
                      <Link2 size={12} strokeWidth={2} aria-hidden />
                      {mapped.itflow_client_name ?? `ITFlow #${mapped.itflow_client_id}`}
                      {mapped.itflow_client_rate && ` · ${mapped.itflow_client_rate}/hr`}
                    </span>
                  ) : (
                    <span className="text-small text-text-tertiary">Not mapped</span>
                  )}
                </div>

                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  {mapped ? (
                    <Button variant="ghost" onClick={() => unlink.mutate(mapped.id)}>
                      <Link2Off size={14} strokeWidth={1.5} aria-hidden />
                      Unlink
                    </Button>
                  ) : (
                    <>
                      {suggested && (
                        <Button
                          onClick={() =>
                            link.mutate({
                              client_id: client.id,
                              itflow_client_id: suggested.itflow_client_id,
                            })
                          }
                        >
                          <ArrowLeftRight size={14} strokeWidth={1.5} aria-hidden />
                          Link to {suggested.name}
                        </Button>
                      )}
                      <select
                        aria-label={`Map ${client.name} to an ITFlow client`}
                        value=""
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          if (value) {
                            link.mutate({ client_id: client.id, itflow_client_id: value });
                          }
                        }}
                        className="min-h-touch rounded border border-border bg-surface-1 px-2 text-small text-text outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border"
                      >
                        <option value="">Map to…</option>
                        {unmappedRemote.map((r) => (
                          <option key={r.itflow_client_id} value={r.itflow_client_id}>
                            {r.name}
                          </option>
                        ))}
                      </select>
                      <Button variant="ghost" onClick={() => push.mutate(client.id)}>
                        <Plus size={14} strokeWidth={1.5} aria-hidden />
                        Create in ITFlow
                      </Button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        {data.chronos.length === 0 && (
          <p className="text-small text-text-tertiary">
            No billable clients yet. The Personal client is never listed here.
          </p>
        )}
      </section>

      {unmappedRemote.length > 0 && (
        <section className="flex flex-col gap-2">
          <h3 className="text-small font-medium text-text-secondary">
            In ITFlow only
          </h3>
          <ul className="flex flex-col gap-2">
            {unmappedRemote.map((remote) => (
              <li
                key={remote.itflow_client_id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface-1 p-3"
              >
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate text-body text-text">{remote.name}</span>
                  {remote.rate && (
                    <span className="text-small text-text-tertiary">
                      {remote.rate}/hr
                    </span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  onClick={() => pull.mutate(remote.itflow_client_id)}
                >
                  <Plus size={14} strokeWidth={1.5} aria-hidden />
                  Create in Chronos
                </Button>
              </li>
            ))}
          </ul>
          <p className="max-w-prose text-small text-text-tertiary">
            These stay in ITFlow unless you want them here. Not every client needs a
            counterpart.
          </p>
        </section>
      )}
    </div>
  );
}
